<script setup lang="ts">
import { computed, onMounted } from "vue";

import { toggleActions } from "@/action/toggleActions";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import PageHeader from "@/components/menu/PageHeader.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SkipButton from "@/components/ui/a11y/SkipButton.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import { isFlexBoard, isRiskBoard, isSolutionBoard, isTeamBoard } from "@/model/board";
import { useBoardStore } from "@/store/board";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useSignalStore } from "@/store/search/signals";
import { isSignalsEnabled } from "@/utils/env/feature";
import { key } from "@/utils/shortcut/key";

import SearchBar from "./SearchBar.vue";
import ArtItem from "./dropdown/ArtItem.vue";
import AssigneeItem from "./dropdown/AssigneeItem.vue";
import DependencyLinkItem from "./dropdown/DependencyLinkItem.vue";
import DependencyTeamItem from "./dropdown/DependencyTeamItem.vue";
import IterationItem from "./dropdown/IterationItem.vue";
import ReporterItem from "./dropdown/ReporterItem.vue";
import RiskyLinksItem from "./dropdown/RiskyLinksItem.vue";
import SignalsItem from "./dropdown/SignalsItem.vue";
import StatusItem from "./dropdown/StatusItem.vue";
import StickersItem from "./dropdown/StickersItem.vue";
import StickyTypeItem from "./dropdown/StickyTypeItem.vue";
import TeamItem from "./dropdown/TeamItem.vue";
import UpperLevelLinks from "./dropdown/UpperLevelLinks.vue";

const menuStore = useSearchMenuStore();

const { registerShortcut } = useShortcuts();

const showDependencyTeamFilter = computed(() => menuStore.showDependencyTeamFilter);
const showUpperLevelLinks = computed(() => useBoardStore().currentBoard().type === "team");
const isOnSolutionBoard = computed(() => isSolutionBoard(useBoardStore().currentBoard().type));
const anyFilterCriteria = computed(() => menuStore.anyFilterCriteria);
const signalsEnabled = isSignalsEnabled();
const showSignals = computed(() => useSignalStore().signals.length > 0);

const showAssigneeAndReporter = computed(() => {
  const currentBoard = useBoardStore().currentBoard().type;
  return isTeamBoard(currentBoard) || isRiskBoard(currentBoard) || isFlexBoard(currentBoard);
});

onMounted(() => {
  registerShortcut(
    { key: key("Escape"), exec: toggle },
    { prevent: false }, // Preventing default stops enter from acting as a click (globally)
  );
});

/**
 * Move focus to the first section of search results
 */
function focusOnResults() {
  document
    .getElementById("search-results-panel")!
    .querySelector<HTMLElement>("[data-search-results-anchor]")
    ?.focus();
}

function returnFocusToSearchBar() {
  // search stays open when the board is changed
  if (!menuStore.isSearchOpen) {
    document.getElementById("search-bar-trigger")!.focus();
  }
}

function toggle() {
  toggleActions.toggleSearch("topbar-menu");
}

function hide() {
  toggleActions.toggleSearch("topbar-menu", true);
}
</script>

<template>
  <div
    v-focus-trap="{
      extraElements: ['search-results-panel'],
      allowOutsideClick: true,
      onPostDeactivate: returnFocusToSearchBar,
    }"
  >
    <PageHeader :title="$t('action.search')" icon="general/search" @close="toggle">
      <SearchBar v-model="menuStore.text" @toggle="toggle" />
      <SkipButton @click="focusOnResults">
        {{ $t("label.skipToResults") }}
      </SkipButton>
      <StickyTypeItem :store="menuStore" />
      <template v-if="menuStore.isDependencySearch">
        <DependencyLinkItem />
        <MenuItemDelimiter />
      </template>
      <StickersItem />
      <IterationItem :store="menuStore" />
      <AssigneeItem v-if="showAssigneeAndReporter" />
      <ReporterItem v-if="showAssigneeAndReporter" />
      <TeamItem v-if="!isOnSolutionBoard" />
      <ArtItem v-else />
      <template v-if="showDependencyTeamFilter">
        <DependencyTeamItem />
        <MenuItemDelimiter />
      </template>
      <UpperLevelLinks v-if="showUpperLevelLinks" />
      <StatusItem />
      <template v-if="!signalsEnabled || showSignals">
        <MenuItemDelimiter />
        <SignalsItem v-if="signalsEnabled" />
        <RiskyLinksItem v-else />
      </template>
      <template v-if="anyFilterCriteria">
        <MenuItemDelimiter />
        <BaseButton color="primary" variant="ghost" @click="hide">
          {{ $t("searchItem.hideSearch") }}
        </BaseButton>
      </template>
    </PageHeader>
  </div>
</template>

<style lang="scss">
.search-select {
  &.dropdown-content {
    width: 200px;
  }
}
</style>
